import type { Card, ProductType, ShippingSupply } from "@/context/AppContext";
import type {
  DeckFormat,
  DeckIntent,
  DeckProfile,
  ProductDeckBuild,
  ProductDeckLine,
  MissingDeckCard,
  DeckSubstitution,
  DeckInventoryMatch,
  ParsedDeckLine,
} from "@/types/deckBuilder";
import {
  CASUAL_BEGINNER_DECK_PROFILE,
  DECK_CORE_PROFILE,
  KID_FRIENDLY_DECK_PROFILE,
} from "@/types/deckBuilder";
import type {
  ListingType,
  ProductOpportunity,
} from "@/types/productOpportunity";
import type { ExternalDeckSource } from "@/types/deckSource";
import { parseDeckList } from "@/features/deckListParser";
import { matchDeckListToInventory } from "@/features/deckInventoryMatcher";
import { scoreDeck } from "@/features/deckScoring";
import { buildPricedEconomics } from "@/features/productPricingProfiles";
import { estimateFulfillment } from "@/features/fulfillmentEstimator";
import { computeBuyerValueScore } from "@/features/buyerValueScore";

export interface BuildDeckProductInput {
  pastedDeckList: string;
  inventoryCards: Card[];
  intent: DeckIntent;
  format: DeckFormat;
  themeKeyword?: string;
  externalSource?: ExternalDeckSource;
  supplies?: ShippingSupply[];
}

export interface BuildDeckProductResult {
  opportunity: ProductOpportunity;
  parsedLines: ParsedDeckLine[];
  matches: DeckInventoryMatch[];
}

export function buildDeckProduct(
  input: BuildDeckProductInput,
): BuildDeckProductResult {
  const parsedLines = parseDeckList(input.pastedDeckList);
  const matches = matchDeckListToInventory(parsedLines, input.inventoryCards);

  // Decide product type based on completeness of the matched deck.
  const profile = pickProfileForIntent(input.intent);
  const totalRequested = matches.reduce((s, m) => s + m.requestedQty, 0);
  const totalIncluded = matches.reduce((s, m) => s + m.ownedQty, 0);
  const missingCount = matches.reduce((s, m) => s + m.missingQty, 0);

  const productType = pickDeckProductType(
    profile,
    totalRequested,
    totalIncluded,
    missingCount,
  );

  const matchedCards = collectMatchedCards(matches);
  const cardCount = matchedCards.reduce((s, c) => s + c.quantity, 0);

  const market = matchedCards.reduce(
    (s, c) => s + (c.marketPrice ?? 0) * (c.quantity ?? 1),
    0,
  );
  const cost = matchedCards.reduce(
    (s, c) => s + (c.costBasis ?? 0) * (c.quantity ?? 1),
    0,
  );
  const fulfillment = estimateFulfillment({
    cards: matchedCards,
    productType,
    supplies: input.supplies,
    cardCountOverride: profile.targetCards,
  });
  const economics = buildPricedEconomics({
    estimatedMarketValue: market,
    estimatedCostBasis: cost,
    productType,
    fulfillmentCost: fulfillment.totalFulfillmentCost,
  });

  // Theme keyword falls back to the most-requested non-energy Pokémon name.
  const themeKeyword = input.themeKeyword ?? autoDetectTheme(matches);

  const score = scoreDeck({
    matches,
    profile,
    themeKeyword,
    expectedMarginPct: economics.expectedMarginPct,
  });

  const deckList: ProductDeckLine[] = matches.map((match) => ({
    cardId: match.matchedCards[0]?.id,
    name: match.requested.name,
    requestedQty: match.requestedQty,
    includedQty: match.ownedQty,
    role: match.requested.section ?? "Unknown",
    required: true,
    sourceLine: match.requested.raw,
  }));

  const missingCards: MissingDeckCard[] = matches
    .filter((m) => m.missingQty > 0)
    .map((m) => ({
      name: m.requested.name,
      missingQty: m.missingQty,
      role: m.requested.section ?? "Unknown",
      priority: m.requested.section === "Energy" ? "Optional" : "Required",
    }));

  const substitutesUsed: DeckSubstitution[] = matches
    .filter((m) => m.matchConfidence < 0.9 && m.matchConfidence > 0)
    .flatMap((m) =>
      m.matchedCards.map((c) => ({
        requestedName: m.requested.name,
        substituteCardId: c.id,
        substituteName: c.name,
        reason: m.matchReason,
      })),
    );

  const deckBuild: ProductDeckBuild = {
    format: input.format,
    intent: input.intent,
    typeTheme: themeKeyword,
    sourceDeckUrl: input.externalSource?.sourceUrl,
    sourceDeckName: input.externalSource?.sourceName,
    sourceProvider: input.externalSource?.provider,
    isCompleteDeck:
      productType === "Pre-built Deck" && totalIncluded >= profile.targetCards,
    legalityVerified: false, // MVP: always false; product copy never claims legality.
    deckList,
    missingCards,
    substitutesUsed,
    deckScore: score,
  };

  const listing = pickDeckListingType(productType, matchedCards);

  const reasons = buildReasons({
    productType,
    profile,
    totalIncluded,
    matches,
    score: score.finalScore,
    economics: economics.expectedProfit,
    listing,
  });

  const warnings: string[] = [...score.warnings];
  if (economics.expectedProfit < 0) {
    warnings.push(
      `Expected profit is negative ($${economics.expectedProfit.toFixed(2)}).`,
    );
  }
  if (productType === "Pre-built Deck" && totalIncluded < profile.targetCards) {
    warnings.push(
      `Deck claims ${profile.targetCards} cards but only ${totalIncluded} are matched — consider Deck Core instead.`,
    );
  }

  const title = buildTitle(productType, themeKeyword, totalIncluded);
  const description = buildDeckDescription({
    productType,
    deckBuild,
    cards: matchedCards,
    profile,
    totalIncluded,
  });

  const opportunity: ProductOpportunity = {
    id: `deck-${productType.toLowerCase().replace(/\s+/g, "-")}-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 7)}`,
    productType,
    strategyId: pickStrategyId(productType),
    title,
    description,
    cards: matchedCards,
    cardIds: matchedCards.map((c) => c.id),
    cardCount,
    estimatedMarketValue: market,
    estimatedCostBasis: cost,
    expectedSalePrice: economics.expectedSalePrice,
    estimatedFulfillmentCost: fulfillment,
    expectedProfit: economics.expectedProfit,
    expectedMarginPct: economics.expectedMarginPct,
    buyerValueScore: computeBuyerValueScore(matchedCards, productType),
    confidenceScore: Math.max(score.finalScore, 30),
    recommendedListingType: listing.type,
    listingTypeReason: listing.reason,
    reasons,
    warnings,
    targetCount: profile.targetCards,
    availabilityPct:
      profile.targetCards <= 0
        ? 0
        : Math.min(
            100,
            Math.round((totalIncluded / profile.targetCards) * 100),
          ),
    deckBuild,
  };

  return { opportunity, parsedLines, matches };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function pickProfileForIntent(intent: DeckIntent): DeckProfile {
  switch (intent) {
    case "beginner-friendly":
      return KID_FRIENDLY_DECK_PROFILE;
    case "deck-core":
    case "upgrade-kit":
      return DECK_CORE_PROFILE;
    default:
      return CASUAL_BEGINNER_DECK_PROFILE;
  }
}

function pickDeckProductType(
  profile: DeckProfile,
  totalRequested: number,
  totalIncluded: number,
  missingCount: number,
): ProductType {
  if (profile.targetCards <= 25) return "Deck Core";
  if (totalIncluded === 0) return "Deck Upgrade Kit";
  if (totalIncluded >= profile.targetCards * 0.95) return "Pre-built Deck";
  if (totalIncluded >= 30) return "Deck Core";
  if (missingCount > totalRequested * 0.5) return "Deck Upgrade Kit";
  return "Deck Core";
}

function collectMatchedCards(matches: DeckInventoryMatch[]): Card[] {
  const seen = new Set<string>();
  const out: Card[] = [];
  for (const match of matches) {
    for (const card of match.matchedCards) {
      if (seen.has(card.id)) continue;
      seen.add(card.id);
      out.push(card);
    }
  }
  return out;
}

function autoDetectTheme(matches: DeckInventoryMatch[]): string | undefined {
  // Prefer the highest-quantity matched non-energy Pokémon line.
  const candidates = matches.filter(
    (m) => m.requested.section !== "Energy" && m.ownedQty > 0,
  );
  if (candidates.length === 0) return undefined;
  candidates.sort((a, b) => b.ownedQty - a.ownedQty);
  return candidates[0].requested.name;
}

function pickDeckListingType(
  productType: ProductType,
  cards: Card[],
): { type: ListingType; reason: string } {
  if (productType === "Pre-built Deck") {
    return {
      type: "FixedPrice",
      reason:
        "Defaulted to Fixed Price — Pre-built Deck is a repeatable product",
    };
  }
  if (productType === "Deck Upgrade Kit") {
    return {
      type: "FixedPrice",
      reason: "Defaulted to Fixed Price — Upgrade Kits are repeatable add-ons",
    };
  }
  // Deck Core: auction when value variance is high (popular Pokémon mix).
  const totalQty = cards.reduce((s, c) => s + (c.quantity ?? 1), 0);
  const totalMarket = cards.reduce(
    (s, c) => s + (c.marketPrice ?? 0) * (c.quantity ?? 1),
    0,
  );
  const avg = totalQty > 0 ? totalMarket / totalQty : 0;
  if (avg > 5) {
    return {
      type: "Auction",
      reason: "Defaulted to Auction — Deck Core contains high-value cards",
    };
  }
  return {
    type: "FixedPrice",
    reason: "Defaulted to Fixed Price — standard Deck Core",
  };
}

function pickStrategyId(productType: ProductType) {
  if (productType === "Pre-built Deck") return "casual-prebuilt-deck" as const;
  if (productType === "Deck Core") return "deck-core" as const;
  return "deck-upgrade-kit" as const;
}

function buildReasons(args: {
  productType: ProductType;
  profile: DeckProfile;
  totalIncluded: number;
  matches: DeckInventoryMatch[];
  score: number;
  economics: number;
  listing: { type: ListingType; reason: string };
}): string[] {
  const reasons: string[] = [];
  reasons.push(`${args.productType} built from imported deck list.`);
  reasons.push(
    `Matched ${args.totalIncluded} cards from current inventory (target ${args.profile.targetCards}).`,
  );
  const missing = args.matches.reduce((s, m) => s + m.missingQty, 0);
  if (missing > 0) {
    reasons.push(`Missing ${missing} cards from the original list.`);
  }
  if (args.score >= 70) reasons.push(`Strong deck score (${args.score}/100).`);
  if (args.economics > 0) reasons.push(`Profitable after fees and shipping.`);
  reasons.push(args.listing.reason);
  return reasons;
}

function buildTitle(
  productType: ProductType,
  themeKeyword: string | undefined,
  totalIncluded: number,
): string {
  const theme = themeKeyword ? `${themeKeyword} ` : "";
  if (productType === "Pre-built Deck") {
    return `60-Card Casual ${theme}Pokemon Deck - Ready to Play Beginner TCG Deck`;
  }
  if (productType === "Deck Core") {
    return `${theme}Deck Core - Pokemon TCG Starter Build / Upgrade Kit`;
  }
  return `Pokemon TCG ${theme}Deck Upgrade Kit - ${totalIncluded} Cards`;
}

interface DeckDescriptionInput {
  productType: ProductType;
  deckBuild: ProductDeckBuild;
  cards: Card[];
  profile: DeckProfile;
  totalIncluded: number;
}

/**
 * Build a deck-aware listing description. Avoids any tournament-legal claims
 * unless deckBuild.legalityVerified === true (always false in MVP).
 */
export function buildDeckDescription(input: DeckDescriptionInput): string {
  const sectionCounts = countSections(input.deckBuild);
  const conditionSummary = summarizeCondition(input.cards);
  const theme = input.deckBuild.typeTheme ?? "casual playable Pokémon TCG";

  const lines: string[] = [];

  if (input.productType === "Pre-built Deck") {
    lines.push(
      "This is a ready-to-play casual Pokémon TCG deck built from real Pokémon cards.",
    );
    lines.push("");
    lines.push("Includes:");
    lines.push(`- ${input.totalIncluded} total cards`);
    lines.push("- Pokémon, Trainer, and Energy cards");
    lines.push(`- Built around: ${theme}`);
    lines.push(
      "- Great for beginners, casual play, or learning the Pokémon TCG",
    );
    lines.push("");
    lines.push("Deck composition:");
    lines.push(`- Pokémon: ${sectionCounts.Pokemon}`);
    lines.push(`- Trainer: ${sectionCounts.Trainer}`);
    lines.push(`- Energy: ${sectionCounts.Energy}`);
    lines.push("");
    lines.push("Condition:");
    lines.push(`Cards are ${conditionSummary} unless otherwise noted.`);
    lines.push("");
    lines.push(
      "Note: this is intended as a fun casual deck. Tournament legality is not guaranteed unless explicitly stated.",
    );
  } else if (input.productType === "Deck Core") {
    lines.push(
      "This is a Pokémon TCG deck core / upgrade kit, not a complete 60-card deck.",
    );
    lines.push("");
    lines.push("Includes:");
    lines.push(`- ${input.totalIncluded} cards`);
    lines.push(`- Key Pokémon and Trainer pieces for a ${theme} build`);
    lines.push("- Great starting point for customizing your own deck");
    lines.push("");
    lines.push(
      "Missing cards may be needed to make a full legal 60-card deck.",
    );
  } else {
    lines.push(
      "This Pokémon TCG upgrade kit adds support and staple pieces to an existing deck.",
    );
    lines.push("");
    lines.push("Includes:");
    lines.push(`- ${input.totalIncluded} cards`);
    lines.push(`- Trainer staples and key pieces inspired by ${theme} builds`);
    lines.push(
      "- Best paired with an existing deck — not a complete 60-card product on its own.",
    );
  }

  if (input.deckBuild.sourceProvider === "tcgplayer") {
    lines.push("");
    lines.push(
      "This product was built using a deck-list style structure inspired by popular Pokémon TCG deck references. Exact tournament legality is not guaranteed unless specifically stated.",
    );
  }

  return lines.join("\n");
}

function countSections(deckBuild: ProductDeckBuild): Record<string, number> {
  const out: Record<string, number> = {
    Pokemon: 0,
    Trainer: 0,
    Energy: 0,
    Unknown: 0,
  };
  for (const line of deckBuild.deckList) {
    out[line.role] = (out[line.role] ?? 0) + line.includedQty;
  }
  return out;
}

function summarizeCondition(cards: Card[]): string {
  const counts: Record<string, number> = {};
  let total = 0;
  for (const c of cards) {
    counts[c.condition] = (counts[c.condition] ?? 0) + (c.quantity ?? 1);
    total += c.quantity ?? 1;
  }
  if (total === 0) return "NM (Near Mint)";
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  const dominant = sorted[0]?.[0] ?? "NM";
  return `mostly ${dominant}`;
}
